/*
 * Copyright (c) 2024, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

export * from '@salesforce/retail-react-app/app/utils/product-utils';

import {
  findLowestPrice,
  getPriceData as basePriceData,
} from '@salesforce/retail-react-app/app/utils/product-utils';
import {NOTIFY_ME_BASE_URL} from '@amblique/king-living/app/constants';
import {resolveMarkupHtml, stripHtmlToPlainText} from '@amblique/king-living/app/utils/utils';
import logger from '@salesforce/retail-react-app/app/utils/logger-instance';

const isNumber = (value) => typeof value === 'number';

/** Currency amounts — max 2 decimal places (SCAPI promo floats, JS sum drift). */
export const roundCurrencyAmount = (value) => {
  const n = Number(value);

  return Number.isFinite(n) ? Math.round(n * 100) / 100 : null;
};

export function isProductCustomFlagTrue(value) {
  return value === true || value === 'true';
}

/**
 * Parse SFRA `c_priceProcessed` from a product or search-hit `representedProduct`.
 *
 * @param {Object} product
 * @returns {Object|null}
 */
const parsePriceProcessed = (product) => {
  const raw = product?.c_priceProcessed ?? product?.representedProduct?.c_priceProcessed;

  if (!raw) return null;

  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;

    return parsed != null && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * PLP-08 / KINDW-1555: sale chrome only when list (standard) price > sale price.
 * When list <= sale (e.g. list A$0, sale A$1), show list only — no badge, no strikethrough.
 *
 * @param {{listPrice: number|null, salePrice: number|null}} pair
 * @returns {{currentPrice: number|null, listPrice: number|null, isOnSale: boolean}}
 */
const resolveListSaleDisplay = ({listPrice, salePrice}) => {
  if (isNumber(listPrice) && isNumber(salePrice)) {
    if (listPrice > salePrice) {
      return {currentPrice: salePrice, listPrice, isOnSale: true};
    }

    return {currentPrice: listPrice, listPrice: null, isOnSale: false};
  }

  if (isNumber(listPrice)) {
    return {currentPrice: listPrice, listPrice: null, isOnSale: false};
  }

  if (isNumber(salePrice)) {
    return {currentPrice: salePrice, listPrice: null, isOnSale: false};
  }

  return {currentPrice: null, listPrice: null, isOnSale: false};
};

/**
 * @param {Object} processed
 * @returns {{listPrice: number|null, salePrice: number|null}}
 */
const pairFromProcessed = (processed) => ({
  listPrice: isNumber(processed.listPrice) ? processed.listPrice : null,
  salePrice: isNumber(processed.salePrice) ?
    processed.salePrice :
    isNumber(processed.min) ?
      processed.min :
      null,
});

/**
 * Read list/sale from `c_priceProcessed` or SCAPI `prices` (productSearch expand=prices).
 *
 * @param {Object} product
 * @param {Object|null} [processed]
 * @returns {{listPrice: number|null, salePrice: number|null}|null}
 */
const extractListSalePair = (product, processed = null) => {
  const parsed = processed ?? parsePriceProcessed(product);

  if (parsed != null) {
    const pair = pairFromProcessed(parsed);

    if (pair.listPrice != null || pair.salePrice != null) {
      return pair;
    }
  }

  const prices = product?.prices ?? product?.representedProduct?.prices;

  if (!prices || typeof prices !== 'object') {
    return null;
  }

  const pair = {
    listPrice: isNumber(prices.listPrice) ? prices.listPrice : null,
    salePrice: isNumber(prices.salePrice) ? prices.salePrice : null,
  };

  return pair.listPrice != null || pair.salePrice != null ? pair : null;
};

/** @param {Object} base @param {Object} resolved */
const applyPriceDisplay = (base, resolved) => {
  const next = {...base, ...resolved};

  if (isNumber(next.currentPrice)) {
    next.currentPrice = roundCurrencyAmount(next.currentPrice);
  }

  if (isNumber(next.listPrice)) {
    next.listPrice = roundCurrencyAmount(next.listPrice);
  }

  return next;
};

/**
 * @param {Object} product
 * @param {Object} opts
 * @param {{listPrice: number|null, salePrice: number|null}} pair
 */
const buildPriceDataFromPair = (product, opts, pair) =>
  applyPriceDisplay(basePriceData(product, opts), resolveListSaleDisplay(pair));

/**
 * Fallback when only tieredPrices exist: OOTB maps min→current, max→list. Inverted books
 * (list A$0, sale A$1) falsely set isOnSale with current=0 — show list only.
 *
 * @param {Object} result
 * @param {Object} product
 */
const applyTieredPriceFallback = (result, product) => {
  const pair = extractListSalePair(product);

  if (pair) {
    return applyPriceDisplay(result, resolveListSaleDisplay(pair));
  }

  const {currentPrice, listPrice, isOnSale} = result;

  if (
    isOnSale &&
    isNumber(currentPrice) &&
    isNumber(listPrice) &&
    currentPrice < listPrice &&
    currentPrice === 0
  ) {
    return applyPriceDisplay(result, {
      currentPrice: 0,
      listPrice: null,
      isOnSale: false,
    });
  }

  return result;
};

/**
 * KINDW-406: master on-sale tiles use priceRanges (low=sale book, high=list book).
 *
 * @param {Object} product
 * @param {Object} result
 */
const applyMasterPriceRanges = (product, result) => {
  const ranges = product?.priceRanges;

  if (!Array.isArray(ranges) || ranges.length < 2) {
    return result;
  }

  const sorted = [...ranges].sort((a, b) => a.minPrice - b.minPrice);

  return applyPriceDisplay(
    result,
    resolveListSaleDisplay({
      listPrice: sorted[sorted.length - 1].minPrice,
      salePrice: sorted[0].minPrice,
    })
  );
};

/**
 * @param {Object} product
 * @param {Object} processed
 */
const buildPriceDataFromProcessed = (product, processed) => {
  const resolved = resolveListSaleDisplay(pairFromProcessed(processed));
  const isRange =
    processed.type === 'range' &&
    isNumber(processed.min) &&
    isNumber(processed.max) &&
    processed.min !== processed.max;

  return {
    currentPrice: resolved.currentPrice != null ? roundCurrencyAmount(resolved.currentPrice) : null,
    listPrice: resolved.listPrice != null ? roundCurrencyAmount(resolved.listPrice) : null,
    pricePerUnit: product?.pricePerUnit,
    isOnSale: resolved.isOnSale,
    isASet: product?.hitType === 'set' || !!product?.type?.set,
    isMaster: product?.hitType === 'master' || !!product?.type?.master,
    isRange,
    tieredPrice: null,
    maxPrice: processed.max ?? null,
  };
};

/**
 * Override of `getPriceData` (KINDW-406 master list price, KINDW-1555 / PLP-08 sale badge rules).
 *
 * @param {Object} product
 * @param {Object} [opts]
 * @returns {Object}
 */
export const getPriceData = (product, opts = {}) => {
  const processed = parsePriceProcessed(product);

  if (processed != null && (processed.salePrice != null || processed.min != null)) {
    return buildPriceDataFromProcessed(product, processed);
  }

  const pair = extractListSalePair(product);

  if (pair) {
    return buildPriceDataFromPair(product, opts, pair);
  }

  const base = basePriceData(product, opts);
  const result = applyTieredPriceFallback(base, product);

  if (result.isOnSale && result.isMaster) {
    return applyMasterPriceRanges(product, result);
  }

  return result;
};

/**
 * PDP / ATC display price for a single SCAPI product (e.g. in-stock finished good).
 * Mirrors ProductView `priceData`: price books + promo overlay when promo beats sale.
 *
 * @param {Object|null|undefined} product
 * @param {Object} [opts]
 * @param {Object|null} [opts.basePriceData] - Optional shell to spread (e.g. master priceData)
 * @returns {Object|null}
 */
export const buildProductDisplayPriceData = (product, opts = {}) => {
  if (!product) return null;

  const {basePriceData: shell = null, quantity = 1} = opts;
  const base = getPriceData(product, {quantity});
  const effective = getProductEffectivePrice(product);

  if (!base && effective == null) return null;

  let currentPrice = base?.currentPrice ?? null;
  let listPrice = base?.listPrice ?? null;
  let isOnSale = Boolean(base?.isOnSale);

  if (effective != null && currentPrice != null && effective < currentPrice) {
    const wasPrice = listPrice ?? currentPrice;

    currentPrice = effective;
    listPrice = wasPrice > effective ? wasPrice : null;
    isOnSale = wasPrice > effective;
  } else if (effective != null && currentPrice == null) {
    currentPrice = effective;
    listPrice = getProductListPrice(product);
    isOnSale = listPrice != null && listPrice > currentPrice;
  }

  if (currentPrice == null) return null;

  return {
    ...(shell || {}),
    ...base,
    currentPrice,
    listPrice,
    isOnSale,
  };
};

/**
 * Find variant that matches the given variation parameters
 * @param {Object} product - Product object with variants
 * @param {Object} variationParams - Object with variation attribute IDs as keys and values
 * @returns {Object|null} Matching variant or null
 */
export const findVariantByParams = (product, variationParams) => {
  if (!product?.variants || !variationParams) {
    return null;
  }

  const matchingVariants = product.variants.filter((variant) => {
    if (!variant.variationValues) return false;

    return Object.keys(variationParams).every(
      (key) => variant.variationValues[key] === variationParams[key]
    );
  });

  return matchingVariants.length > 0 ? matchingVariants[0] : null;
};

/**
 * Get price data for a specific variation value
 * @param {Object} product - Product object with variants
 * @param {Object} currentVariationParams - Current variation parameters
 * @param {string} variationAttributeId - The variation attribute ID (e.g., 'size', 'color')
 * @param {string} variationValue - The new value to get price for
 * @param {Object} opts - Options for getPriceData (quantity, etc.)
 * @returns {Object|null} Price data object or null
 */
export const getVariantPriceForValue = (
  product,
  currentVariationParams,
  variationAttributeId,
  variationValue,
  opts = {}
) => {
  if (!product || !variationAttributeId || !variationValue) {
    return null;
  }

  const newVariationParams = {
    ...currentVariationParams,
    [variationAttributeId]: variationValue,
  };

  const variant = findVariantByParams(product, newVariationParams);

  if (variant) {
    return getPriceData(variant, opts);
  }

  // If no exact match, try to find variants that partially match
  // This handles cases where not all attributes are selected yet
  // We'll find the first variant that has this value for this attribute
  if (!product?.variants) {
    return null;
  }

  const partialMatchVariants = product.variants.filter((v) => {
    if (!v.variationValues) return false;

    const hasThisValue = v.variationValues[variationAttributeId] === variationValue;
    const matchesOtherParams = Object.keys(currentVariationParams).every(
      (key) => key === variationAttributeId || v.variationValues[key] === currentVariationParams[key]
    );

    return hasThisValue && matchesOtherParams;
  });

  if (partialMatchVariants.length > 0) {
    return getPriceData(partialMatchVariants[0], opts);
  }

  return null;
};

/**
 * Get the variant price data more directly
 * @param {Object} variant - Variant object
 * @param {Object} opts - Options for getPriceData
 * @returns {Object} Price data
 */
export const getVariantPriceData = (variant, opts = {}) => {
  if (!variant) return null;

  return getPriceData(variant, opts);
};

/**
 * Get the first orderable variant from a list of variants
 * @private
 * @param {Array} variants - Array of variant objects
 * @returns {Object|null} First orderable variant or null
 */
const getFirstOrderableVariant = (variants) => {
  if (!variants?.length) return null;

  return variants.find((variant) => variant?.orderable) || null;
};

/**
 * Find a variant by matching attributes, with fallback to first orderable
 * @private
 * @param {Array} variants - Array of variant objects
 * @param {Object} attributes - Attribute key-value pairs to match
 * @returns {Object|null} Matching variant or first orderable variant
 */
const findVariantByAttributes = (variants, attributes) => {
  if (!variants?.length) return null;

  const hasAttributes = attributes && Object.keys(attributes).length > 0;

  if (!hasAttributes) {
    return getFirstOrderableVariant(variants);
  }

  const matchedVariant = variants.find((variant) => {
    if (!variant?.variationValues || !variant.orderable) return false;

    return Object.keys(attributes).every(
      (key) => variant.variationValues[key] === attributes[key]
    );
  });

  return matchedVariant || getFirstOrderableVariant(variants);
};

/**
 * Select the first orderable variant based on URL parameters
 * @param {Object} product - Product object with variants
 * @param {Object} urlParams - URL parameters for variant selection
 * @returns {Object} Object containing selectedVariant and error (if any)
 */
export const selectFirstOrderableVariant = (product, urlParams) => {
  if (!product?.variants?.length) {
    return {
      selectedVariant: null,
      error: product ? 'Invalid product data: missing variants information' : null,
    };
  }

  const matchedVariant = findVariantByAttributes(product.variants, urlParams);

  if (!matchedVariant) {
    const hasAttributes = urlParams && Object.keys(urlParams).length > 0;

    return {
      selectedVariant: null,
      error: hasAttributes ?
        'No matching variant found for selected attributes' :
        'No orderable variants available for this product',
    };
  }

  return {
    selectedVariant: matchedVariant,
    error: null,
  };
};

/**
 * Determine if a product is a simple product (finished good) or complex product (requires configuration).
 *
 * A product is considered **complex** when either:
 *  - `c_isGuidedPDP` is `true`, OR
 *  - `productLinks` array has at least one entry
 *
 * Everything else is treated as a simple product.
 *
 * @param {Object} product - Product object from SCAPI
 * @returns {boolean} True if simple product, false if complex product
 */
/**
 * Determine if a product is a simple product.
 *
 * Simple product criteria (independent check):
 * - c_isGuidedPDP === false (explicit simple flag), OR
 * - No productLinks (productLinks array is empty or not present)
 *
 * @param {Object} product - Product object from SCAPI
 * @returns {boolean} True if the product is a simple product
 */
/**
 * Determine if a product is a complex (guided) product.
 *
 * UI only: `c_isGuidedPDP === true` shows pre-defined Sets (no cover swatch
 * grid). Inventory family (A / B / FG) is **not** this flag — use
 * `getInventoryProductFamily` / visible options.
 *
 * @param {Object} product - Product object from SCAPI
 * @returns {boolean} True if the product is a complex product
 */
export const isComplexProduct = (product) => {
  if (!product) {
    return false;
  }

  return product.c_isGuidedPDP === true;
};

/**
 * Determine if a product is a simple product.
 * Simple product is the inverse of complex product - they are mutually exclusive.
 *
 * @param {Object} product - Product object from SCAPI
 * @returns {boolean} True if the product is a simple product
 */
export const isSimpleProduct = (product) => {
  return !isComplexProduct(product);
};

/**
 * Determine if a product is a "Simple Raw Product" — without any options.
 *
 * A Simple Raw Product is defined as:
 *  - No recommendations (no suggested/related products)
 *  - No linked products (no product links or bundles)
 *
 * This is used for "Finished Good" products that show a simplified PDP layout
 * with inventory status and "Notify Me" support.
 *
 * @param {Object} product - Product object from SCAPI
 * @returns {boolean} True if the product has no recommendations or linked products
 */
export const isSimpleRawProduct = (product) => {
  if (!product) return false;

  const hasRecommendations =
    Array.isArray(product.recommendations) && product.recommendations.length > 0;
  const hasLinkedProducts =
    Array.isArray(product.productLinks) && product.productLinks.length > 0;

  return !hasRecommendations && !hasLinkedProducts;
};

/**
 * Determine if a product should show Cylindo 3D viewer
 *
 * Logic:
 * - Simple products: Never show 3D (always show image cycler)
 * - Complex products: Show 3D only if images are from Cylindo source
 *
 * @param {Object} product - Product object from SCAPI
 * @param {string} imageSource - Image source ('pim' or 'cylindo')
 * @returns {boolean} True if should show Cylindo 3D viewer
 *
 * @example
 * const complexProduct = { c_simpleProduct: false };
 * shouldShowCylindo3D(complexProduct, 'cylindo'); // returns true
 * shouldShowCylindo3D(complexProduct, 'pim'); // returns false
 *
 * const simpleProduct = { c_simpleProduct: true };
 * shouldShowCylindo3D(simpleProduct, 'cylindo'); // returns false (simple always shows cycler)
 */
export const shouldShowCylindo3D = (product, imageSource) => {
  if (!product || !imageSource || isSimpleProduct(product)) {
    return false;
  }

  // Complex products: Show 3D only if images are from Cylindo
  return imageSource === 'cylindo';
};

/**
 * Parse a raw OGU JSON string into the standard container array, filtering hidden items.
 *
 * @param {string|null} raw - JSON string of OGU containers
 * @returns {Array|null} Processed containers or null
 */
function parseRawOguString(raw) {
  if (!raw || typeof raw !== 'string') {
    return null;
  }

  try {
    const parsed = JSON.parse(raw);

    if (!Array.isArray(parsed)) {
      return null;
    }

    const withVisibleValues = parsed
      .filter((item) => item && item.isHidden !== true)
      .map((item) => ({
        containerId: item.containerId,
        containerName: item.containerName,
        containerDescription: item.containerDescription,
        values: (item.values || []).filter((v) => v && v.isHidden !== true).map((v) => ({
          recommendationTypeId: v.recommendationTypeId,
          isRequired: v.isRequired === true,
        })),
      }));

    return withVisibleValues.filter((container) => container.values.length > 0);
  } catch (err) {
    logger.error('[parseRawOguString] Failed to parse JSON — check for syntax errors (missing commas, trailing commas, etc.)', {error: err.message, raw});

    return null;
  }
}

/**
 * Parse and enrich c_optionsGroupingUI into a processed structure.
 * Does NOT mutate product.c_optionsGroupingUI — raw value is kept for reference.
 *
 * Point 12 (visibility): Exclude values (container groups) with isHidden; exclude containers
 * that have no visible values (all hidden).
 *
 * @param {Object} product - Product object with c_optionsGroupingUI (raw string)
 * @returns {Array|null} Processed array [{ containerId, containerName, values: [...] }] or null
 */
export const processOptionsGroupingUI = (product) => {
  return parseRawOguString(product?.c_optionsGroupingUI);
};

/**
 * Current `c_optionsGroupingUIProcessed` wrapper schema from SFRA (king_living product.actions).
 * Legacy responses may be a raw array of containers (no wrapper) — still supported.
 * v2: `extra` container splits values (isRequired:true) from optionalAddOns (isRequired:false).
 */
export const OGU_PROCESSED_SCHEMA_VERSION = 2;

/**
 * Normalise parsed JSON from c_optionsGroupingUIProcessed to { version, containers }.
 *
 * @param {unknown} parsed
 * @returns {{version: number, containers: Array}|null}
 */
function normalizeOptionsGroupingUIProcessedPayload(parsed) {
  if (parsed == null) {
    return null;
  }

  if (Array.isArray(parsed)) {
    return {version: 0, containers: parsed};
  }

  if (
    typeof parsed === 'object' &&
    Array.isArray(parsed.containers)
  ) {
    const v = parsed.oguSchemaVersion;

    return {
      version: v != null && !Number.isNaN(Number(v)) ? Number(v) : OGU_PROCESSED_SCHEMA_VERSION,
      containers: parsed.containers,
    };
  }

  return null;
}

function warnUnexpectedOguSchema(version) {
  if (typeof console !== 'undefined' && console.warn) {
    console.warn(
      `[getOptionsGroupingUIProcessed] Unsupported oguSchemaVersion ${version}; expected ${OGU_PROCESSED_SCHEMA_VERSION}. Using containers anyway.`
    );
  }
}

/**
 * Get processed options grouping UI enriched with product data from recommendations.
 * SFRA pre-enriches this as c_optionsGroupingUIProcessed for complex (isGuidedPDP) products.
 * New shape: `{ oguSchemaVersion: 1, containers: [...] }`. Legacy: root JSON array.
 * Each slot value contains a products[] array with recommendation product fields
 * (productId, name, price, …) and slot-level flags (isVisible, isRequired, isHidden).
 *
 * @param {Object} product - Product object with c_optionsGroupingUIProcessed
 * @returns {Array|null} Enriched containers array, or null
 */
export const getOptionsGroupingUIProcessed = (product) => {
  if (!product) {
    return null;
  }

  const enriched = product.c_optionsGroupingUIProcessed;

  if (enriched) {
    // Already a parsed object (Commerce SDK auto-parses JSON custom attributes)
    if (Array.isArray(enriched)) {
      if (enriched.length > 0) {
        return enriched;
      }

      return null;
    }

    if (typeof enriched === 'object') {
      const norm = normalizeOptionsGroupingUIProcessedPayload(enriched);

      if (norm && Array.isArray(norm.containers)) {
        if (
          norm.version > 0 &&
          norm.version !== OGU_PROCESSED_SCHEMA_VERSION
        ) {
          warnUnexpectedOguSchema(norm.version);
        }

        return norm.containers;
      }
    }

    // Parse if still a string
    if (typeof enriched === 'string') {
      try {
        const parsed = JSON.parse(enriched);
        const norm = normalizeOptionsGroupingUIProcessedPayload(parsed);

        if (norm && Array.isArray(norm.containers)) {
          if (
            norm.version > 0 &&
            norm.version !== OGU_PROCESSED_SCHEMA_VERSION
          ) {
            warnUnexpectedOguSchema(norm.version);
          }

          return norm.containers;
        }
      } catch {
        // fall through to raw fallback
      }
    }
  }

  // Note (TSD §7 — Finished Goods): finished goods are NOT auto-routed through
  // master `c_optionsGroupingUIProcessed` here. They render via `<FinishedGoodOptionsList>`
  // using master OGU when resolved (see `FinishedGoodView`).

  // Fallback: parse raw optionsGroupingUI (no products[], no isVisible flags)
  return processOptionsGroupingUI(product);
};

/**
 * Returns optional add-on slot values from the `extra` container.
 *
 * Schema v2+: reads `container.optionalAddOns` (SFRA pre-split by isRequired === false).
 * Schema v0/v1 fallback: filters `container.values` by isRequired === false so the PWA
 * stays backward-compatible when SFRA and PWA are deployed at different times.
 *
 * @param {Object} product - Product object with c_optionsGroupingUIProcessed
 * @returns {Array} Enriched slot values where isRequired is false, or empty array
 */
export const getOptionalAddOns = (product) => {
  const containers = getOptionsGroupingUIProcessed(product) || [];
  const extra = containers.find((c) => c.containerId === 'extra');

  if (!extra) return [];

  // Schema v2: SFRA pre-split optional add-ons into a dedicated array
  if (Array.isArray(extra.optionalAddOns)) {
    return extra.optionalAddOns;
  }

  // Schema v0/v1 fallback: derive from values by isRequired flag
  return (extra.values || []).filter((v) => v.isRequired === false);
};

/**
 * Trim a PIM name line or product name for display.
 *
 * @param {unknown} value
 * @returns {string}
 */
export function trimProductNameLine(value) {
  if (value == null) return '';

  const s = typeof value === 'string' ? value : String(value);

  return s.trim();
}

/**
 * Option product catalog name for selection labels.
 *
 * @param {Object|null|undefined} product
 * @returns {string}
 */
export function resolveOptionProductName(product) {
  return trimProductNameLine(product?.name);
}

/**
 * Option product shortDescription as plain text (HTML stripped).
 *
 * @param {Object|null|undefined} product
 * @returns {string}
 */
export function resolveOptionProductShortDescriptionPlain(product) {
  const raw =
    typeof product?.shortDescription === 'string' ? product.shortDescription : '';

  return stripHtmlToPlainText(raw);
}

/**
 * Shelves display: PIM often stores "Light & Charge Shelf - Natural Oak"; design uses a comma.
 *
 * @param {string|null|undefined} name
 * @returns {string}
 */
export function formatShelvesSelectionName(name) {
  if (typeof name !== 'string') return '';

  return name.replace(/\s*-\s*/g, ', ');
}

/**
 * Shelves slot-card / selection primary label: catalog `name` only.
 * shortDescription is for the option picker radio rows, not the committed slot card.
 * Legacy names like "Standard Shelf - Natural Oak" are normalized to commas.
 *
 * @param {Object|null|undefined} product
 * @returns {string|null}
 */
export function resolveShelvesPrimaryName(product) {
  const name = formatShelvesSelectionName(resolveOptionProductName(product));

  return name || null;
}

/**
 * Primary category display name from SFCC parentCategoryTree (KL-982).
 *
 * @param {Array<{name?: string}>|null|undefined} categoryTree
 * @returns {string|null}
 */
export function resolvePrimaryCategoryName(categoryTree) {
  const last = categoryTree?.[categoryTree.length - 1];

  return trimProductNameLine(last?.name) || null;
}

/**
 * PDP base-information heading (KL-982 / DE-228 Point 1).
 * Scenario 1 (Choose your layout visible): primary category name.
 * Scenario 2 (Choose your layout hidden): product name as header, shortDescription as sub-header.
 *
 * `hideChooseYourLayout` = Scenario 2 when the layout block is not shown: BM hide flag,
 * finished-good without resolved master, or (standard PDP) layout fetch incomplete / empty list.
 *
 * @param {Object|null|undefined} product
 * @param {{hideChooseYourLayout?: boolean, categoryTree?: Array<{name?: string}>}} [options]
 * @returns {{title: string|null, subtitle: string|null}}
 */
export function resolvePdpBaseInformationTitle(product, {hideChooseYourLayout = false, categoryTree} = {}) {
  if (!product) {
    return {title: null, subtitle: null};
  }

  if (!hideChooseYourLayout) {
    return {
      title: resolvePrimaryCategoryName(categoryTree),
      subtitle: null,
    };
  }

  const title = trimProductNameLine(product.name) || null;
  const subtitle = stripHtmlToPlainText(resolveMarkupHtml(product.shortDescription)) || null;

  return {title, subtitle};
}

/**
 * True when an optionsGroupingUI container has multiple recommendation types (slot-cards layout).
 * Matches buildSteps() layoutType decision (values.length > 1).
 *
 * @param {Object|null|undefined} container
 * @returns {boolean}
 */
export function isMultiTypeOptionsContainer(container) {
  if (!container?.values?.length) return false;

  return container.values.length > 1;
}

/** Extra-option HTML headings removed; follow-up is Contentful. */
export const getContainerBlockInfo = () => null;

/**
 * Promo callout copy for PLP tiles (RangeProductTile). Mirrors OOTB `PromoCallout` but also reads
 * `representedProduct.productPromotions` on SCAPI search hits. Returns deduped HTML/plain strings
 * (lowest-price promotion first, then any other promotions with callout copy).
 *
 * @param {Object} product - Product or search hit from SCAPI
 * @returns {string[]}
 */
export const getProductPromoCalloutMessages = (product) => {
  if (!product) return [];

  const seen = new Set();
  const messages = [];

  const addPromoMessage = (promo) => {
    if (!promo) return;

    const html = promo.calloutMsg?.trim();
    const text = html || promo.promotionName?.trim();

    if (!text || seen.has(text)) return;

    seen.add(text);
    messages.push(text);
  };

  addPromoMessage(findLowestPrice(product)?.promotion);

  const promoLists = [product.productPromotions, product.representedProduct?.productPromotions].filter(
    Boolean
  );

  for (const promos of promoLists) {
    for (const promo of promos) {
      addPromoMessage(promo);
    }
  }

  return messages;
};

/**
 * Returns the lowest promotional price from productPromotions, or null.
 *
 * @param {Object} product - Product object from SCAPI (with promotions expanded)
 * @returns {number|null}
 */
export const getProductPromotionalPrice = (product) => {
  const promos = product?.productPromotions;

  if (!promos?.length) return null;

  const prices = promos
    .map((p) => p.promotionalPrice)
    .filter((p) => p != null && p > 0);

  if (!prices.length) return null;

  return Math.min(...prices);
};

/**
 * Returns the effective price for a product fetched via useProducts/SCAPI.
 * Checks promotional prices first (lowest promotionalPrice wins when below
 * the price book price), then falls back to product.price or priceRanges.
 *
 * @param {Object} product - Product object from SCAPI
 * @returns {number|null} Effective price or null
 */
export const getProductEffectivePrice = (product) => {
  if (!product) return null;

  const basePrice = product.price;
  const promoPrice = getProductPromotionalPrice(product);

  if (promoPrice != null && (basePrice == null || promoPrice < basePrice)) {
    return roundCurrencyAmount(promoPrice);
  }

  if (basePrice != null) return roundCurrencyAmount(basePrice);

  // Master products expose priceRanges instead of a direct price
  const minPrice = product.priceRanges?.[0]?.minPrice;

  if (minPrice != null) return roundCurrencyAmount(minPrice);

  return null;
};

/**
 * Returns the list/original price (RRP) to strike through, or null when there is
 * no discount to show.
 *
 * Two discount shapes are recognised:
 *   1. Price-book discount — `c_priceProcessed.listPrice > salePrice`.
 *   2. Promotion-only discount — there is NO price-book discount
 *      (`listPrice === salePrice`, common when a product has no sale book), but a
 *      `productPromotions` promotion drives the effective price below the RRP. In
 *      this case the RRP is still the correct strikethrough/"was" price.
 *
 * Without (2), promotion-only products (e.g. J2-T78_LIGHT_CHARGE: list = sale =
 * 111.33, promo → 100.2) would return null, so list totals would fall back to the
 * promo price and understate the saving.
 *
 * @param {Object} product - Product object from SCAPI (has c_priceProcessed and/or productPromotions)
 * @returns {number|null}
 */
export const getProductListPrice = (product) => {
  if (!product) return null;

  let listPrice = null;
  let salePrice = null;

  const raw = product.c_priceProcessed;

  if (raw) {
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;

      listPrice = parsed?.listPrice ?? null;
      salePrice = parsed?.salePrice ?? null;
    } catch {
      // malformed — safe to ignore
    }
  }

  // (1) Price-book discount: root book RRP is above the active sale book price.
  if (listPrice != null && salePrice != null && listPrice > salePrice) {
    return listPrice;
  }

  // (2) Promotion-only discount: no price-book discount, but a promotion makes the
  // effective price lower than the RRP. Surface the RRP so strikethrough + saving show.
  const rrp = listPrice ?? salePrice ?? product.price ?? null;
  const effective = getProductEffectivePrice(product);

  if (rrp != null && effective != null && rrp > effective) {
    return rrp;
  }

  return null;
};

/**
 * Returns true when a simple product has recommendation-based configurable options
 * (c_optionsGroupingUIProcessed or c_coverGroupsData populated).
 *
 * These products use CustomizeStepsInline instead of VariationAttributesSimple.
 *
 * @param {Object} product - Product object from SCAPI
 * @returns {boolean} True if the product has configurable options
 */
export const hasConfigurableOptions = (product) => {
  if (!product) return false;

  const simple = isSimpleProduct(product);

  if (!simple) return false;

  const optionContainers = getOptionsGroupingUIProcessed(product);
  const hasOptionGroups = Array.isArray(optionContainers) && optionContainers.length > 0;
  const hasCoverData = product.c_coverGroupsData != null;

  return hasOptionGroups || hasCoverData;
};

function parseJsonAttributeList(raw) {
  if (raw == null || raw === '') return null;
  if (Array.isArray(raw)) return raw;

  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);

      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  return null;
}

function isVisibleOguNode(node) {
  return Boolean(node) && node.isHidden !== true && node.isVisible !== false;
}

/**
 * Master / freeform has shopper-selectable cover variants (DE-274).
 *
 * @param {Object|null|undefined} product
 * @returns {boolean}
 */
export const hasMasterCoverVariants = (product) => {
  if (!product) return false;

  // SFRA attaches the master's c_allVariantsData / cover-group enrichment to
  // variant SKU responses too (?pid=<variant>). Only masters are shopper-
  // selectable for covers — otherwise Build & Customise shows with no cover UI.
  const isMaster = product.type?.master === true || product.type === 'master';

  if (!isMaster) return false;

  const fromAllVariants = parseJsonAttributeList(product.c_allVariantsData);

  if (fromAllVariants?.length > 0) return true;

  if (Array.isArray(product.variants) && product.variants.length > 0) return true;

  const coverGroups = parseJsonAttributeList(product.c_coverGroupsData);

  return Boolean(coverGroups?.length > 0);
};

/**
 * Simple product has at least one **visible** OGU option slot (DE-274).
 * Hidden containers/slots and optional-add-on-only extras do not count.
 *
 * @param {Object|null|undefined} product
 * @returns {boolean}
 */
export const hasVisibleSimpleProductOptions = (product) => {
  const containers = getOptionsGroupingUIProcessed(product);

  if (!Array.isArray(containers) || containers.length === 0) return false;

  for (const container of containers) {
    if (!isVisibleOguNode(container)) continue;
    if (container.containerId === 'cover' || container.containerId === 'optionalAddOns') continue;

    const slots = Array.isArray(container.values) ? container.values : [];

    for (const slot of slots) {
      if (!isVisibleOguNode(slot)) continue;

      // v0/v1 extra.values still include KingGuard-style optional add-ons.
      if (container.containerId === 'extra' && slot.isRequired === false) continue;

      if (Array.isArray(slot.products)) {
        if (slot.products.some((p) => p && p.isHidden !== true)) return true;
        continue;
      }

      // Raw OGU fallback has no products[] — a visible slot is enough.
      if (slot.recommendationTypeId != null) return true;
    }
  }

  return false;
};

/**
 * PDP information tab row (Build & Customise / Package details + Specifications).
 * Complex and finished-good PDPs keep the row. Simple PDPs show it only when the
 * master has covers or the product has visible options (DE-274).
 *
 * @param {Object|null|undefined} product
 * @param {{isComplexProduct?: boolean, isFinishedGood?: boolean}} [flags]
 * @returns {boolean}
 */
export const shouldShowPdpInformationTabs = (
  product,
  {isComplexProduct = false, isFinishedGood = false} = {}
) => {
  if (isComplexProduct || isFinishedGood) return true;

  return hasMasterCoverVariants(product) || hasVisibleSimpleProductOptions(product);
};

/**
 * Simple product type constants
 * will adjust based on the logic in the future task
 */
export const SIMPLE_PRODUCT_TYPE = Object.freeze({
  PLAIN: 'simplePlain',
  COLOR_SIZE_OPTIONS: 'simpleColorSizeOptions',
  MULTIPLE_CUSTOMIZE_STEPS: 'simpleMultipleCustomizeSteps',
  FREE_FORM: 'simpleFreeForm',
  CONFIGURABLE_MASTER: 'simpleConfigurableMaster',
  FINISHED_GOOD: 'simpleFinishedGood',
});

/**
 * Check if a product is a finished good (TSD - PDP Simple §7 / FSD §7).
 *
 * Finished goods are simple products with **pre-selected** recommendation products
 * baked in (Figma: Jasper / 1977). They mirror the linked-product / popular-set shape
 * used by Complex PDPs but live as a single SKU whose price already includes the
 * recommendations.
 *
 * Identifier (FG full): BOTH `coverVariantId` AND non-empty `c_selectedOptions`.
 *
 * Simple FG: `c_packageMasterId` / `c_coverVariantId` + empty/absent
 * `c_selectedOptions` + `longDescription` — still routed as finished-good PDP.
 *
 * @param {Object} product - Product object from SCAPI
 * @returns {boolean} True if the product is a finished good
 */
export const isFinishedGood = (product) => {
  if (!product) return false;

  const selectedOptions = product.c_selectedOptions;
  const hasSelectedOptions = Array.isArray(selectedOptions) && selectedOptions.length > 0;

  if (getFinishedGoodCoverVariantId(product) && hasSelectedOptions) {
    return true;
  }

  // Simple FG — keep previous full-only check above intact.
  const longDescription = product.longDescription || product.c_longDescription;
  const hasLongDescription =
    (typeof longDescription === 'string' && longDescription.trim().length > 0) ||
    (longDescription && typeof longDescription === 'object' && longDescription.source);
  const hasPackageMaster = Boolean(product.c_packageMasterId);
  const hasCoverVariant = Boolean(getFinishedGoodCoverVariantId(product));

  return Boolean(!hasSelectedOptions && hasLongDescription && (hasPackageMaster || hasCoverVariant));
};

/**
 * Resolve the cover variation product id for a finished good (`c_coverVariantId`).
 *
 * @param {Object} product
 * @returns {string|null}
 */
export const getFinishedGoodCoverVariantId = (product) => {
  if (!product) return null;

  return product.c_coverVariantId ?? null;
};

/**
 * Resolve the master product id for a finished good from its cover variation product.
 *
 * @param {Object|null} coverVariant - Cover variation product (GET via `c_coverVariantId`)
 * @returns {string|null}
 */
export const getFinishedGoodMasterProductId = (coverVariant) => {
  if (!coverVariant) return null;

  return coverVariant.master?.masterId ?? coverVariant.c_masterProductId ?? null;
};

/**
 * Detect the specific type of a simple product.
 * This function categorizes simple products based on their structure:
 * - Variation products (master with variants)
 * - Recommendation products (products with recommendation options)
 * - Product options
 *
 * @param {Object} product - Product object from SCAPI
 * @returns {string|null} The simple product type constant, or null if not a simple product
 *
 */
export const detectSimpleProductType = (product) => {
  if (!product) return null;

  const simple = isSimpleProduct(product);

  if (!simple) return null;

  // Finished goods (TSD §7) are detected up-front so we never fall through to
  // the recommendation-count branches below. The bundle's own
  // `c_optionsGroupingUIProcessed` is typically empty/absent; display uses master OGU
  // via `<FinishedGoodOptionsList>` when the master is resolved.
  if (isFinishedGood(product)) {
    return SIMPLE_PRODUCT_TYPE.FINISHED_GOOD;
  }

  const isMaster = product.type?.master === true || product.type === 'master';
  const hasVariations = Boolean(product.variants && product.variants.length > 0);

  const optionContainers = getOptionsGroupingUIProcessed(product);
  const hasRecommendations = Array.isArray(optionContainers) && optionContainers.length > 0;

  let requiredRecommendationCount = 0;

  if (hasRecommendations) {
    optionContainers.forEach((container) => {
      if (Array.isArray(container.values)) {
        container.values.forEach((value) => {
          if (value.isRequired === true) {
            requiredRecommendationCount += 1;
          }
        });
      }
    });
  }

  if (!hasVariations && !hasRecommendations) {
    return SIMPLE_PRODUCT_TYPE.PLAIN;
  }

  if (isMaster && hasVariations && hasRecommendations) {
    return SIMPLE_PRODUCT_TYPE.CONFIGURABLE_MASTER;
  }

  if (isMaster && hasVariations) {
    return SIMPLE_PRODUCT_TYPE.FREE_FORM;
  }

  if (requiredRecommendationCount === 1) {
    return SIMPLE_PRODUCT_TYPE.COLOR_SIZE_OPTIONS;
  }

  if (requiredRecommendationCount >= 2) {
    return SIMPLE_PRODUCT_TYPE.MULTIPLE_CUSTOMIZE_STEPS;
  }

  return SIMPLE_PRODUCT_TYPE.PLAIN;
};

/**
 * Opens the Notify Me form in a new tab with product details pre-filled.
 * Used when a product is out of stock and the customer wants to be notified
 * when it becomes available.
 *
 * @param {Object} product - Product object with productId/id and name
 * @param {string} [product.productId] - Product ID (preferred)
 * @param {string} [product.id] - Alternative product ID field
 * @param {string} [product.name] - Product name
 */
export const openNotifyMeForm = () => {
  window.open(
    `${NOTIFY_ME_BASE_URL}`,
    '_blank',
    'noopener,noreferrer'
  );
};

/**
 * Normalize a product dimension custom attribute value.
 *
 * @param {string|number|null|undefined} value
 * @returns {string|null}
 */
function normalizeProductDimensionValue(value) {
  if (value == null || value === '') {
    return null;
  }

  const normalized = String(value).trim();

  return normalized || null;
}

/**
 * Read Height / Width / Depth from a SCAPI product (supports legacy and c_ keys).
 *
 * @param {Object|null|undefined} product
 * @returns {{depth: string|null, height: string|null, width: string|null}}
 */
export function getProductDimensionValues(product) {
  if (!product) {
    return {depth: null, height: null, width: null};
  }

  return {
    depth: normalizeProductDimensionValue(product.dimDepth ?? product.c_dimDepth),
    height: normalizeProductDimensionValue(product.dimHeight ?? product.c_dimHeight),
    width: normalizeProductDimensionValue(product.dimWidth ?? product.c_dimWidth),
  };
}

/**
 * Format product dimensions for order line items (FSD ACCDET-05 / order confirmation).
 * Uses W x D x H when all three exist; otherwise joins available values with " x ".
 *
 * @param {Object|null|undefined} product
 * @returns {string|null}
 */
export function formatProductDimensions(product) {
  const {depth, height, width} = getProductDimensionValues(product);

  if (!width && !depth && !height) {
    return null;
  }

  if (width && depth && height) {
    return `W ${width} x D ${depth} x H ${height}`;
  }

  const parts = [];

  if (width) parts.push(width);
  if (depth) parts.push(depth);
  if (height) parts.push(height);

  return parts.length ? parts.join(' x ') : null;
}

/**
 * Whether the PDP Specifications accordion has renderable content
 * (PIM/legacy fields or dimension image). Mirrors InformationAccordion sections.
 *
 * @param {Object} [product]
 * @returns {boolean}
 */
export const hasProductSpecificationsContent = (product) => {
  if (!product) {
    return false;
  }

  const {depth, height, width} = getProductDimensionValues(product);
  const hasAccordionSections = Boolean(
    product.c_pimlyFeatureOverview ||
    product.longDescription ||
    product.c_pimlyWarrantyInformation ||
    product.c_pimlyProductSupport ||
    product.c_whatsIncluded ||
    height ||
    width ||
    depth
  );

  return hasAccordionSections || Boolean(product.c_pimlyDimensionImage);
};

/**
 * Product lead time in days from the SFCC `leadTime` custom attribute
 * (`c_leadTime` / `leadTime` in SCAPI product payloads).
 *
 * @param {Object|null|undefined} product
 * @returns {number}
 */
export function getProductLeadTimeDays(product) {
  const raw = product?.c_leadTime ?? product?.leadTime ?? 0;
  const parsed = Number(raw);

  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

/**
 * Optional `c_leadTime` fields for Shopper Baskets add-item payloads (KINDW-1792).
 *
 * @param {Object|null|undefined} product
 * @returns {{c_leadTime?: number}}
 */
export function basketLineLeadTimeFields(product) {
  const days = getProductLeadTimeDays(product);

  return days > 0 ? {c_leadTime: days} : {};
}
